import { useState } from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, BookOpen, HandHeart, Map, User, Compass, Shield, Search } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { SEARCH_UI } from "@/data/quickSearchI18n";
import { QuickSearch } from "@/components/search/QuickSearch";

// Short phone labels where the full i18n label wraps onto two lines in the 6-tab bar.
const SHORT = { times: { id: "Salat", en: "Prayer", ar: "الصلاة", fr: "Prière", de: "Gebet", es: "Oración", ru: "Намаз", zh: "礼拜", ja: "礼拝", ko: "예배", hi: "नमाज़" } };

const baseItems = [
  { to: "/", icon: Home, key: "home", testId: "nav-home" },
  { to: "/guide", icon: BookOpen, key: "guide", testId: "nav-guide" },
  { to: "/prayers", icon: HandHeart, key: "prayers", testId: "nav-prayers" },
  { to: "/times", icon: Compass, key: "times", testId: "nav-times" },
  { to: "/map", icon: Map, key: "map", testId: "nav-map" },
  { to: "/profile", icon: User, key: "profile", testId: "nav-profile" },
];

const useNavItems = () => {
  const { user } = useAuth();
  return user?.role === "admin"
    ? [...baseItems, { to: "/admin", icon: Shield, key: "admin", testId: "nav-admin" }]
    : baseItems;
};

export const BottomNav = () => {
  const { t } = useTranslation();
  const { tc } = useLang();
  const items = useNavItems();
  const [searchOpen, setSearchOpen] = useState(false);
  const left = items.slice(0, 3);
  const right = items.slice(3);
  const linkClass = "flex min-h-[48px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 py-1.5 text-muted-foreground";
  const renderItem = ({ to, icon: Icon, key, testId }) => (
    <NavLink key={key} to={to} end={to === "/"} data-testid={testId} className={linkClass}>
      {({ isActive }) => (
        <>
          <motion.span animate={{ scale: isActive ? 1.15 : 1, y: isActive ? -1 : 0 }} className={isActive ? "text-primary" : ""}>
            <Icon className="h-5 w-5" strokeWidth={isActive ? 2.5 : 1.8} />
          </motion.span>
          <span className={`max-w-full truncate px-0.5 text-[10px] font-semibold leading-tight ${isActive ? "text-primary" : ""}`}>
            {SHORT[key] ? tc(SHORT[key]) : t(key)}
          </span>
        </>
      )}
    </NavLink>
  );
  return (
    <>
      <nav className="fixed inset-x-0 z-40 px-3 md:hidden bottom-nav-safe">
        <div className="mx-auto flex max-w-md items-stretch justify-around glass rounded-full border border-white/50 shadow-[0_8px_30px_rgb(0,0,0,0.12)] px-1 py-1">
          {left.map(renderItem)}
          <button type="button" onClick={() => setSearchOpen(true)} data-testid="nav-search" aria-label={tc(SEARCH_UI.search)}
            className={`${linkClass} text-primary`}>
            <span className="flex h-9 w-9 -mt-4 items-center justify-center rounded-full bg-primary text-white shadow-lg shadow-primary/30 ring-4 ring-background">
              <Search className="h-4 w-4" strokeWidth={2.5} />
            </span>
            <span className="max-w-full truncate px-0.5 text-[10px] font-semibold leading-tight">{tc(SEARCH_UI.search)}</span>
          </button>
          {right.map(renderItem)}
        </div>
      </nav>
      <QuickSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
};

export const TopNav = ({ right }) => {
  const { t } = useTranslation();
  const items = useNavItems();
  return (
    <div className="hidden md:flex items-center gap-1">
      {items.map(({ to, key, testId }) => (
        <NavLink
          key={key}
          to={to}
          end={to === "/"}
          data-testid={`${testId}-desktop`}
          className={({ isActive }) =>
            `rounded-full px-4 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"
            }`
          }
        >
          {t(key)}
        </NavLink>
      ))}
    </div>
  );
};
